import { IQuestionnaireDetailed } from '../interfaces/Questionnaire/IQuestionnaireDetailed';
import {
  GetAnswerDto,
  IGetAnswerQuestionnaire,
} from '../interfaces/Questionnaire/IQuestionnaireAnswers';
import { QuestionnaireHistoryItem } from '../interfaces/Questionnaire/IQuestionnaireHistory';

interface RawAnswerShape {
  questionId?: string;
  questionTemplateId?: string;
  questionTemplate?: string;
  questionText?: string;
  questionLabel?: string;
  questionName?: string;
  value?: string;
  answer?: string;
  answerText?: string;
  selectedValue?: string;
  questionType?: string;
  type?: string;
}

export function buildQuestionnaireDetailFromHistoryItem(
  item: QuestionnaireHistoryItem,
  questionnaireTemplateId?: number
): IQuestionnaireDetailed | null {
  const questionnaire =
    item.questionnaire ??
    ((item.submission as Record<string, unknown> | null)?.[
      'questionnaire'
    ] as IQuestionnaireDetailed['questionnaire']) ??
    null;

  if (!questionnaire) {
    return null;
  }

  const answers = extractAnswers(item);

  return {
    id: Number(item.questionnaireId ?? item.answerId ?? item.id ?? 0),
    questionnaireTemplateId:
      Number(item.questionnaireTemplateId ?? questionnaireTemplateId ?? 0) || 0,
    answerId: item.answerId ? Number(item.answerId) : undefined,
    patientId: item.patientId ? Number(item.patientId) : undefined,
    visitId: item.visitId ? Number(item.visitId) : undefined,
    episodeCareId: item.episodeCareId ? Number(item.episodeCareId) : undefined,
    creationDate: item.createdDate ?? item.creationDate,
    questionnaire,
    answers: {
      id: Number(item.answerId ?? item.questSubmissionId ?? item.id ?? 0),
      questionnaireTemplateId:
        Number(item.questionnaireTemplateId ?? questionnaireTemplateId ?? 0) || 0,
      answer: answers,
    },
    isSubmitted: item.isSubmitted ?? true,
  };
}

function extractAnswers(item: QuestionnaireHistoryItem): GetAnswerDto[] {
  const rawAnswers: GetAnswerDto[] = [];

  if (Array.isArray(item.answers)) {
    (item.answers as GetAnswerDto[]).forEach((answer: GetAnswerDto) => {
      rawAnswers.push(answer);
    });
  }

  const nestedAnswers = item.answers as IGetAnswerQuestionnaire | null;
  if (nestedAnswers?.answer && Array.isArray(nestedAnswers.answer)) {
    nestedAnswers.answer.forEach((answer: GetAnswerDto) => {
      rawAnswers.push(answer);
    });
  }

  const questionnaireData = extractQuestionnaireData(item);
  Object.entries(questionnaireData).forEach(([questionId, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    if (
      rawAnswers.some(
        (answer: GetAnswerDto) => answer.questionTemplateId?.toString() === questionId
      )
    ) {
      return;
    }

    rawAnswers.push({
      questionTemplateId: Number(questionId),
      questionTemplate: questionId,
      questionType: inferQuestionType(value),
      value: normalizeAnswerValue(value),
    });
  });

  normalizeAnswerCollection(
    (item.submission as Record<string, unknown> | null)?.['answers'] ??
      (item.submission as Record<string, unknown> | null)?.[
        'questionnaireSubmissionAnswers'
      ] ??
      item.questionnaireSubmissionAnswers ??
      parseAnswersJson(item.answersJson)
  ).forEach((answer: RawAnswerShape) => {
    const questionId = answer.questionId ?? answer.questionTemplateId;
    const normalizedValue =
      answer.value ??
      answer.answer ??
      answer.answerText ??
      answer.selectedValue ??
      '';

    if (!questionId || normalizedValue === '') {
      return;
    }

    if (
      rawAnswers.some(
        (existing: GetAnswerDto) =>
          existing.questionTemplateId?.toString() === questionId.toString()
      )
    ) {
      return;
    }

    rawAnswers.push({
      questionTemplateId: Number(questionId),
      questionTemplate:
        answer.questionTemplate ??
        answer.questionText ??
        answer.questionLabel ??
        answer.questionName ??
        questionId.toString(),
      questionType:
        answer.questionType ?? answer.type ?? inferQuestionType(normalizedValue),
      value: normalizeAnswerValue(normalizedValue),
    });
  });

  return rawAnswers;
}

function extractQuestionnaireData(
  item: QuestionnaireHistoryItem
): Record<string, unknown> {
  if (item.questionnaireData && typeof item.questionnaireData === 'object') {
    return item.questionnaireData;
  }

  const submission = item.submission as Record<string, unknown> | null;
  const submissionData = submission?.['questionnaireData'];

  return submissionData && typeof submissionData === 'object'
    ? (submissionData as Record<string, unknown>)
    : {};
}

function normalizeAnswerCollection(source: unknown): RawAnswerShape[] {
  if (Array.isArray(source)) {
    return source.filter(
      (entry): entry is RawAnswerShape => !!entry && typeof entry === 'object'
    );
  }

  if (source && typeof source === 'object') {
    const sourceRecord = source as Record<string, unknown>;

    if (Array.isArray(sourceRecord['answers'])) {
      return normalizeAnswerCollection(sourceRecord['answers']);
    }

    if (Array.isArray(sourceRecord['items'])) {
      return normalizeAnswerCollection(sourceRecord['items']);
    }
  }

  return [];
}

function parseAnswersJson(
  answersJson: QuestionnaireHistoryItem['answersJson']
): unknown {
  if (typeof answersJson === 'string') {
    try {
      return JSON.parse(answersJson);
    } catch {
      return null;
    }
  }

  return answersJson ?? null;
}

function inferQuestionType(value: unknown): string {
  const normalized = normalizeAnswerValue(value).toLowerCase();
  return normalized === 'true' || normalized === 'false' ? 'boolean' : 'text';
}

function normalizeAnswerValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.join(',');
  }

  return value?.toString() ?? '';
}
